import {
    computed, type SetupContext, watch, unref
} from 'vue';
import { useGetters, useState } from 'vuex-composition-helpers';
import { filterByBonusMiles, hasNonBonusMilesOperators } from '@features/tourProduct/filterTourProducts';
import { type MapForFiltersState } from '@ve/components/hotel/filters/common/store/types';
import { type IMapForSearchDataGetters } from '@ve/pages/serp/store';
import { NAMESPACE as MAP_FOR_SEARCH_DATA } from '@ve/pages/serp/store/SearchDataStore';
import { ProductType } from '@ve/types/ProductType';
import _ from 'underscore';
import { getPrepaymentsList } from '@features/firstPaymentDefinition/filterByPrepayments';

const filtersMappingStoreName = 'mapForFilters';

export default ({ emit }: SetupContext) => {
    const {
        isExclusive,
        products,
        productType,
        directFlight,
        bonusMiles,
        operators,
        prepayments,
    } = useState<MapForFiltersState>(filtersMappingStoreName, ['isExclusive', 'warranted', 'products', 'productType', 'directFlight', 'bonusMiles', 'operators', 'prepayments']);

    const {
        isPairOfDepartureCityAndArrivalCountryAreValid,
    } = useGetters<IMapForSearchDataGetters>(MAP_FOR_SEARCH_DATA, ['isPairOfDepartureCityAndArrivalCountryAreValid']);

    const numOfSelected = computed(() => {
        let numOfSelectedFilters = +unref(isExclusive);
        numOfSelectedFilters += unref(directFlight) ? 1 : 0;
        numOfSelectedFilters += unref(prepayments)?.length || 0;
        numOfSelectedFilters += unref(bonusMiles) ? 1 : 0;
        return numOfSelectedFilters;
    });

    const selectedFiltersText = computed(() => {
        let text = 'Доп. параметры';
        if (unref(numOfSelected)) {
            text += `: ${unref(numOfSelected)}`;
        }
        return text;
    });

    const isExclusiveFilterShow = computed(() => unref(isExclusive) || !!_.find(unref(products), (product) => product.isExclusive));

    const isProductTypeAllOrTourOffer = computed(() => unref(productType) === 'all' || unref(productType) === 'tourOffer');

    const directFlightFilterShow = computed(() => unref(isProductTypeAllOrTourOffer) && unref(isPairOfDepartureCityAndArrivalCountryAreValid));

    const additionalFiltersHidden = computed(() => unref(productType) === 'hotelOffer' || unref(productType) === 'railwayTour');

    const isBonusMilesFilterVisible = computed(() => unref(bonusMiles) || !!unref(products)?.find((product) => product.type === ProductType.TourProduct && filterByBonusMiles(product)));
    const uniquePrepaymentsList = computed(() => getPrepaymentsList(unref(products) || []));

    const prepaymentFilterShow = computed(() => unref(uniquePrepaymentsList).length > 0);

    const prepaymentFilterChanged = (prepaymentsValue: number[]) => {
        emit('prepayment-filter-changed', prepaymentsValue);
    };

    const isExclusiveChanged = (isExclusiveValue: boolean) => {
        emit('is-exclusive-filter-changed', isExclusiveValue);
    };

    const directFlightChanged = (directFlightValue: boolean) => {
        emit('direct-flight-changed', directFlightValue);
    };

    const bonusMilesChanged = (bonusMilesFilter: boolean) => {
        emit('bonus-miles-changed', bonusMilesFilter);
    }

    watch(
        operators,
        (selectedOperators) => {
            if (unref(bonusMiles) && hasNonBonusMilesOperators(selectedOperators)) {
                bonusMilesChanged(false);
            }
        },
        { immediate: true }
    );

    return {
        bonusMiles,
        additionalFiltersHidden,
        selectedFiltersText,
        isExclusiveFilterShow,
        isProductTypeAllOrTourOffer,
        isExclusiveChanged,
        directFlightChanged,
        bonusMilesChanged,
        isExclusiveSelected: isExclusive,
        directFlightSelected: directFlight,
        bonusMilesSelected: bonusMiles,
        directFlightFilterShow,
        isBonusMilesFilterVisible,
        numOfSelected,
        selectedOperators: operators,
        prepaymentFilterShow,
        uniquePrepaymentsList,
        prepaymentFilterChanged,
        prepaymentsSelected: prepayments,
    };
};